import { useCompleteWorkOrder } from '@/api/equipment/work-orders/completeWorkOrder';
import { useUncompleteWorkOrder } from '@/api/equipment/work-orders/uncompleteWorkOrder';
import { useUpdateWorkOrder } from '@/api/equipment/work-orders/updateWorkOrder';
import { useDeleteTask } from '@/api/system/tasks/deleteTask';
import { ConfirmationDialog } from '@/components/Elements';
import { FormDate } from '@/components/Form/FormDate';
import { Button } from '@/components/ui/elements/button';
import { Checkbox } from '@/components/ui/elements/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/elements/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/elements/sheet';
import { Form } from '@/components/ui/form/form';
import { useAuthorization } from '@/features/auth/authorization';
import { LocationContext } from '@/providers/location';
import { WorkOrder } from '@/types';
import { cn } from '@/utils/format';
import { useContext, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useMaintainables } from '../../hooks/useMaintainables';
import { WorkOrderStatusBadge } from './WorkOrderStatusBadge';
import { WorkOrderSubtasksSection } from './WorkOrderSubtasksSection';

interface WorkOrderEditSheetProps {
  open: boolean;
  workOrder: WorkOrder;
  onClose: () => void;
  onClosed: () => void;
}

interface WorkOrderEditSheetContentProps {
  workOrder: WorkOrder;
  onClose: () => void;
  onClosed: () => void;
}

interface WorkOrderEditSheetBodyProps {
  workOrder: WorkOrder;
  onClose: () => void;
}

const impactOptions = ['Safety', 'Customer Experience', 'Quality'] as WorkOrder['meta']['impact'][];

const LabeledField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
      {label}
    </label>
    {children}
  </div>
);

export const WorkOrderEditSheetBody = ({ workOrder, onClose }: WorkOrderEditSheetBodyProps) => {
  const { activeLocationId } = useContext(LocationContext);
  const { checkPolicy } = useAuthorization();
  const nameRef = useRef<HTMLTextAreaElement>(null);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);

  const { mutate: completeWorkOrder } = useCompleteWorkOrder();
  const { mutate: uncompleteWorkOrder } = useUncompleteWorkOrder();
  const { mutate: updateWorkOrder } = useUpdateWorkOrder();
  const { mutate: deleteTask } = useDeleteTask({ onSuccess: onClose });

  const { equipmentOptions } = useMaintainables({ activeLocationId });

  const canEdit = checkPolicy({ policy: 'equipment.work-orders.update' });
  const canDelete = checkPolicy({ policy: 'equipment.work-orders.delete' });

  const update = (data: Partial<WorkOrder>) => {
    updateWorkOrder({ id: workOrder.task_id, data });
  };

  const handleNameBlur = () => {
    if (!nameRef.current) return;
    const newName = nameRef.current.value.trim();
    if (newName && newName !== workOrder.name) {
      update({ name: newName });
    }
  };

  const handleDescriptionBlur = () => {
    if (!descriptionRef.current) return;
    const newDescription = descriptionRef.current.value;
    if (newDescription !== (workOrder.description ?? '')) {
      update({ description: newDescription || null });
    }
  };

  const toggleCompleted = () => {
    if (workOrder.completed) {
      uncompleteWorkOrder({ task: workOrder });
    } else {
      completeWorkOrder({ task: workOrder });
    }
  };

  const toggleNotOperable = () => {
    update({ meta: { ...workOrder.meta, noop: !workOrder.meta.noop } });
  };

  const form = useForm({
    defaultValues: {
      due_at: workOrder?.due_at ?? '',
    },
  });

  return (
    <Form {...form}>
      <div className="flex flex-col gap-6">
        <SheetHeader>
          <SheetTitle className="sr-only">Edit Work Order</SheetTitle>
        </SheetHeader>

        <WorkOrderStatusBadge workOrder={workOrder} size="lg" />

        {/* Task name */}
        <div>
          <textarea
            ref={nameRef}
            aria-label="Work Order Name"
            key={workOrder.task_id}
            className={cn(
              'w-full text-lg font-semibold bg-transparent border-0 p-0 resize-none focus:ring-0 focus:outline-hidden leading-snug',
              !canEdit && 'pointer-events-none',
            )}
            defaultValue={workOrder.name}
            rows={3}
            disabled={!canEdit}
            onBlur={handleNameBlur}
          />
        </div>

        {/* Description */}
        <LabeledField label="Description">
          <textarea
            ref={descriptionRef}
            aria-label="Description"
            key={`desc-${workOrder.task_id}`}
            className={cn(
              'w-full text-sm bg-muted/40 border rounded-md p-2 resize-none focus:ring-1 focus:ring-ring focus:outline-hidden min-h-20',
              !canEdit && 'pointer-events-none opacity-60',
            )}
            defaultValue={workOrder.description ?? ''}
            rows={3}
            disabled={!canEdit}
            placeholder="Add a description…"
            onBlur={handleDescriptionBlur}
          />
        </LabeledField>

        <div className="grid grid-cols-2 gap-2">
          {/* Completed */}
          <LabeledField label="Completed">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={workOrder.completed}
                onClick={toggleCompleted}
                disabled={!canEdit}
                id="completed-toggle"
              />
              <label
                htmlFor="completed-toggle"
                className="text-sm cursor-pointer select-none"
                onClick={canEdit ? toggleCompleted : undefined}
              >
                {workOrder.completed ? 'Mark as incomplete' : 'Mark as complete'}
              </label>
            </div>
          </LabeledField>

          {/* Not Operable */}
          <LabeledField label="Not Operable">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={workOrder.meta.noop}
                onClick={toggleNotOperable}
                disabled={!canEdit}
                id="not-operable-toggle"
              />
              <label
                htmlFor="not-operable-toggle"
                className="text-sm cursor-pointer select-none"
                onClick={canEdit ? toggleNotOperable : undefined}
              >
                {workOrder.meta.noop ? 'Mark as operable' : 'Mark as not operable'}
              </label>
            </div>
          </LabeledField>
        </div>

        <div className="grid grid-cols-2 gap-2">
          {/* Equipment */}
          <LabeledField label="Equipment">
            <Select
              aria-label="Select equipment"
              onValueChange={(value) =>
                update({
                  taskable_id: parseInt(value.split(':')[1]),
                  taskable_type: value.split(':')[0],
                })
              }
              defaultValue={
                workOrder.taskable_type && workOrder.taskable_id
                  ? `${workOrder.taskable_type}:${workOrder.taskable_id}`
                  : undefined
              }
              disabled={!canEdit}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select equipment…" />
              </SelectTrigger>
              <SelectContent>
                {equipmentOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </LabeledField>

          {/* Impact */}
          <LabeledField label="Impact">
            <Select
              aria-label="Select impact"
              onValueChange={(value) =>
                update({
                  meta: { ...workOrder.meta, impact: value as WorkOrder['meta']['impact'] },
                })
              }
              defaultValue={workOrder.meta.impact ?? undefined}
              disabled={!canEdit}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select impact…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Inbox</SelectItem>
                {impactOptions.map((option) => (
                  <SelectItem key={option} value={option ?? ''}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </LabeledField>
        </div>

        {/* Due Date */}
        <LabeledField label="Due Date">
          {canEdit ? (
            <FormDate
              control={form.control}
              name="due_at"
              aria-label="Due Date"
              onFieldBlur={(value) => update({ due_at: value ?? null })}
            />
          ) : (
            <p className="text-sm text-muted-foreground">
              {workOrder.due_at ? new Date(workOrder.due_at).toLocaleDateString() : '—'}
            </p>
          )}
        </LabeledField>

        <LabeledField label="Subtasks">
          <WorkOrderSubtasksSection workOrder={workOrder} canEdit={canEdit} />
        </LabeledField>

        {/* Footer actions */}
        {canDelete && (
          <div className="mt-auto pt-4 border-t">
            <ConfirmationDialog
              triggerButton={
                <Button variant="destructive" size="sm" className="w-full">
                  Delete Work Order
                </Button>
              }
              confirmButtonText="Delete"
              confirmAction={() => deleteTask({ id: workOrder.task_id })}
              title={`Delete "${workOrder.name}"?`}
              body="This action cannot be undone."
            />
          </div>
        )}
      </div>
    </Form>
  );
};

export const WorkOrderEditSheetContent = ({
  workOrder,
  onClose,
  onClosed,
}: WorkOrderEditSheetContentProps) => {
  return (
    <SheetContent
      onCloseAutoFocus={onClosed}
      side="right"
      className="w-full sm:max-w-md overflow-y-auto"
    >
      <WorkOrderEditSheetBody workOrder={workOrder} onClose={onClose} />
    </SheetContent>
  );
};

export const WorkOrderEditSheet = ({
  open,
  workOrder,
  onClose,
  onClosed,
}: WorkOrderEditSheetProps) => {
  return (
    <Sheet open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <WorkOrderEditSheetContent workOrder={workOrder} onClose={onClose} onClosed={onClosed} />
    </Sheet>
  );
};
